import { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Tabs, TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { CandidatesTablePrework } from '../CandidatesTablePrework';
import { useCreateGroupPrework } from './useCreateGroup';
import { GroupsTablePrework } from './GroupsTable';
import useQueryParams from '../../../hooks/useQueryParams';
import { PREWORKGROUPS } from '../../../shared/Routes';
import { usePreworkGroupId } from '../../../hooks/usePreworkGroupId';
import { PreworkGroupTabs } from '../../../shared/Constants';
import { PreworkGraphicalView } from './PreworkGraphicalView';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { BatchViewPreWork } from './BatchView';
import { useBatchViewPrework } from './useBatchView';
import { CandidatesBatchState } from './CandidatesList.store';
import { useSnapshot } from 'valtio';
import useUserSession from '../../../hooks/useUserSession';
import { hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { NoAccess } from '../../../components/NoAccess';

export const PreWorkCreateGroupContainer = () => {
	const { t } = useTranslation();
	const {
		candidatesList,
		onCandidatesPageChange,
		onCandidateFilterChange,
		candidatesFilter,
		candidatePaging,
		onCreateGroupClick,
		activeTabKey,
		setActiveTabKey,
		groupsList,
		groupsFilter,
		onGroupsFilterChange,
		onGroupsPageChange,
		groupPaging,
		dashboardDetail,
		onDashboardDetailFilterChange,
		dashboardCandidatesList,
		onDashboardPageChange,
		onDashboardFilterChange,
		dashboardPaging,
		dashboardFilter,
	} = useCreateGroupPrework();
	const { navigateWithPreworkBatchIdAndGroupTab } = usePreworkGroupId();
	const queryParams = useQueryParams();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const candidateBatchSnap = useSnapshot(CandidatesBatchState);
	const batchIdInSnap = candidateBatchSnap.selectedBatch?.id;
	const [selectedBatchId, setSelectedBatchId] = useState<number | undefined>(
		batchIdInSnap
	);
	const { candidateBatchData } = useBatchViewPrework();
	const { batches } = candidateBatchData;
	const batchOptions = batches?.map((batch) => ({
		label: batch.name,
		value: batch.id,
	}));

	const hasPrivileges = loggedInUserPrivileges !== undefined;

	const showPreworkCandidates = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_CANDIDATES,
		loggedInUserPrivileges
	);

	const showGroupListView = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_GROUPS,
		loggedInUserPrivileges
	);

	const showViewAllNewCandidates = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_DASHBOARD_METRICS,
		loggedInUserPrivileges
	);

	const showGraphicalView = hasPrivilege(
		PrivilegeCodes.GET_PREWORK_GRAPHICAL_VIEW,
		loggedInUserPrivileges
	);

	const showBatches = hasPrivilege(
		PrivilegeCodes.GET_BATCHES,
		loggedInUserPrivileges
	);

	useEffect(() => {
		if (batches?.length && !batchIdInSnap) {
			CandidatesBatchState.selectedBatch = batches[0];
			sessionStorage.setItem(
				'preworkSelectedBatchID',
				JSON.stringify(batches[0].id)
			);
		}
	}, [batches, batchIdInSnap]);

	const activetab = useMemo(() => {
		const activeTabParam = queryParams.get('preworkActiveTab');
		return activeTabParam;
	}, [queryParams]);

	const showSelectBatch = activetab !== PreworkGroupTabs.BATCHES;

	const functionaryTabs = [];
	if (showPreworkCandidates) {
		functionaryTabs.push({
			key: PreworkGroupTabs.CREATE_GROUP,
			label: t('createGroup'),
			children: (
				<CandidatesTablePrework
					key={PreworkGroupTabs.CREATE_GROUP}
					data={candidatesList}
					onCandidatesPageChange={onCandidatesPageChange}
					candidatePageConfig={candidatePaging}
					candidatesFilter={candidatesFilter}
					onCandidateFilterChange={onCandidateFilterChange}
					onCreateGroupClick={onCreateGroupClick}
					selectedBatchId={batchIdInSnap}
				/>
			),
		});
	}
	if (showGroupListView) {
		functionaryTabs.push({
			key: PreworkGroupTabs.GROUPS_LIST_VIEW,
			label: t('groupListView'),
			children: (
				<GroupsTablePrework
					key={PreworkGroupTabs.GROUPS_LIST_VIEW}
					data={groupsList}
					onGroupsPageChange={onGroupsPageChange}
					groupPageConfig={groupPaging}
					groupsFilter={groupsFilter}
					onGroupsFilterChange={onGroupsFilterChange}
					selectedBatchId={batchIdInSnap}
				/>
			),
		});
	}
	if (showViewAllNewCandidates) {
		functionaryTabs.push({
			key: PreworkGroupTabs.VIEW_ALL_NEW_CANDIDATES,
			label: t('viewAllCandidates'),
			children: (
				<CandidatesTablePrework
					key={PreworkGroupTabs.VIEW_ALL_NEW_CANDIDATES}
					data={dashboardCandidatesList}
					onCandidatesPageChange={onDashboardPageChange}
					candidatePageConfig={dashboardPaging}
					candidatesFilter={dashboardFilter}
					onCandidateFilterChange={onDashboardFilterChange}
					isDashboardTable={true}
					dashboardDetail={dashboardDetail}
					onDashboardDetailFilterChange={onDashboardDetailFilterChange}
					activeTabKey={activeTabKey}
					selectedBatchId={batchIdInSnap}
					viewCandidateMentor={true}
				/>
			),
		});
	}
	if (showGraphicalView) {
		functionaryTabs.push({
			key: PreworkGroupTabs.GRAPHICAL_VIEW,
			label: t('graphicalView'),
			children: (
				<PreworkGraphicalView
					key={PreworkGroupTabs.GRAPHICAL_VIEW}
					selectedBatchId={batchIdInSnap}
				/>
			),
		});
	}

	const groupTabs: TabsProps['items'] =
		selectedRole === UserRole.SNP_ADMIN || showBatches
			? [
					...functionaryTabs,
					{
						key: PreworkGroupTabs.BATCHES,
						label: t('batches'),
						children: <BatchViewPreWork key={PreworkGroupTabs.BATCHES} />,
					},
			  ]
			: functionaryTabs;

	const visibleTabKeys = groupTabs?.map((tab) => tab?.key);

	const urlTab = queryParams.get('preworkActiveTab');

	useEffect(() => {
		if (functionaryTabs.length === 0) return;

		if (!urlTab || !visibleTabKeys.includes(urlTab)) {
			const defaultTab = visibleTabKeys[0];

			if (defaultTab && urlTab !== defaultTab) {
				navigateWithPreworkBatchIdAndGroupTab(PREWORKGROUPS, defaultTab);
			}
		}
	}, [
		urlTab,
		visibleTabKeys,
		functionaryTabs.length,
		navigateWithPreworkBatchIdAndGroupTab,
	]);

	const handleTabClick = useCallback(
		(key: string) => {
			setActiveTabKey(key);
			navigateWithPreworkBatchIdAndGroupTab(PREWORKGROUPS, key);
		},
		[navigateWithPreworkBatchIdAndGroupTab, setActiveTabKey]
	);

	const handleBatchChange = useCallback(
		(value: number) => {
			setSelectedBatchId(value);
			sessionStorage.setItem('preworkSelectedBatchID', JSON.stringify(value));
			const selectedBatch = batches.find((batch) => batch.id === value);
			CandidatesBatchState.selectedBatch = selectedBatch;
		},
		[batches]
	);

	if (!hasPrivileges) {
		return null;
	}

	if (functionaryTabs.length === 0) {
		return <NoAccess />;
	}

	return (
		<div className="p-8">
			{showSelectBatch && (
				<div className="flex items-center gap-2">
					<p className="text-base font-medium">{t('selectBatch')}</p>
					<Select
						className="w-48"
						size="large"
						options={batchOptions}
						value={
							selectedBatchId || (batchOptions?.length && batchOptions[0].value)
						}
						onChange={handleBatchChange}
					/>
				</div>
			)}
			<Tabs
				activeKey={activetab || PreworkGroupTabs.CREATE_GROUP}
				items={groupTabs}
				size="large"
				onTabClick={(key: string) => handleTabClick(key)}
				destroyInactiveTabPane={true}
			/>
		</div>
	);
};
